import { Routes } from "@/api";
import { Intervals } from "@/constants";
import { PortfolioTableHeader, SidebarNavItem } from "@/types";
import { FormatText } from "@/utils/format-text";
import { isNil } from "@/utils/is-nil";
import { ContentVisibility } from "@prisma/client";

export const anonymousPortfolioPlaceholder = "Investor is anonymous";

const portfolioSidebar: SidebarNavItem[] = [
  {
    title: "Portfolio Overview",
    href: Routes.PORTFOLIO_OVERVIEW,
  },
  // {
  //   title: "Holdings",
  //   href: `${Routes.PORTFOLIO_ALLOCATIONS}?tab=table-view`,
  // },
  // {
  //   title: "Analytics",
  //   href: Routes.PORTFOLIO_PERFORMANCE,
  // },
  {
    title: "Transaction History",
    href: Routes.PORTFOLIO_HISTORY,
    isSubscriber: true,
    isAuth: true,
  },
  // {
  //   title: "Portfolio Reports",
  //   href: Routes.PORTFOLIO_REPORTS,
  //   isAuth: true,
  // },
  {
    title: "Notifications",
    href: Routes.PORTFOLIO_NOTIFICATIONS,
  },
  {
    title: "Description",
    href: Routes.PORTFOLIO_DESCRIPTION,
  },
  // {
  //   title: "Risk",
  //   href: Endpoints.PORTFOLIO_RISK,
  // },
  // {
  //   title: "Correlation",
  //   href: Endpoints.PORTFOLIO_CORRELATION,
  // },
];

type GetPortfolioSidebarOptions = {
  isUserPortfolio: boolean;
  isLoggedIn: boolean;
  isTournamentPortfolio: boolean;
  isSubscriber: boolean;
  portfolioId: string;
};

/**
 * Normalizes the href property of a SidebarNavItem based on the provided portfolioId.
 * If the href property is a function, it will be called with the portfolioId as an argument.
 * Otherwise, the href property will be returned as is.
 *
 * @param portfolioId - The ID of the portfolio.
 * @returns A function that takes a SidebarNavItem and returns a modified version of it with the normalized href property.
 */
function normalizeHref(portfolioId: string) {
  return (item: SidebarNavItem) => {
    return {
      ...item,
      href:
        typeof item.href === "function" ? item.href(portfolioId) : item.href,
    };
  };
}

export function getPortfolioSidebar(options: GetPortfolioSidebarOptions) {
  if (options.isUserPortfolio) {
    return portfolioSidebar.map(normalizeHref(options.portfolioId));
  } else {
    let sidebarItems = portfolioSidebar.filter((item) =>
      isNil(item.isAuth) ? true : options.isLoggedIn && item.isAuth,
    );
    if (options.isSubscriber) {
      sidebarItems = sidebarItems.filter((item) => {
        return typeof item.isSubscriber === "boolean"
          ? item.isSubscriber
          : true;
      });
    }

    return sidebarItems.map(normalizeHref(options.portfolioId));
  }
}

export const portfolioTableHeader: PortfolioTableHeader = {
  rank: {
    text: "Rank",
  },
  name: {
    text: "Name",
  },
  author: {
    text: "Author",
  },
  visibility: {
    text: "Visibility",
  },
  positions: {
    text: "Positions",
    tooltip:
      FormatText.stripWhitespace(`Positions represent the individual investments or holdings that comprise a portfolio. 
              In other words, a position is a specific asset, such as a stock, bond, mutual fund, 
              exchange-traded fund (ETF), or any other financial
              instrument, that an investor holds within their portfolio,
              crypto in our case. The quantity or dollar value of each
              asset held determines the size of the position.`),
  },
  allocations: {
    text: "Allocations",
    tooltip:
      FormatText.stripWhitespace(`Allocations refer to how a portfolio is distributed among
                    different asset classes or investment categories. When
                    constructing a portfolio, investors allocate their capital
                    to various investments to achieve specific objectives, such
                    as maximizing returns or minimizing risk. The goal of
                    strategic asset allocation is to create a diversified
                    portfolio that balances risk and return based on an
                    individual's financial goals, risk tolerance, and
                    investment time horizon.`),
  },
  last7Days: {
    text: "Last 7 Days",
    tooltip:
      FormatText.stripWhitespace(`Last 7 Days refers to a specific time frame of the most
                    recent seven days from the current date. In portfolio
                    analysis, the Last 7 Days is often used to examine
                    short-term performance and changes in the portfolio value
                    over the past week. It provides a quick snapshot of how the
                    portfolio has performed recently and can highlight any
                    significant movements or trends in the market. Analyzing the
                    portfolio's performance over the Last 7 Days can help
                    investors identify short-term fluctuations, assess the
                    impact of recent market events, and evaluate the
                    effectiveness of recent investment decisions or changes in
                    allocations.
                  `),
  },
  cagr: {
    text: "CAGR",
    tooltip:
      FormatText.stripWhitespace(`The compound annual growth rate (CAGR) is the rate of return
                    (RoR) that would be required for an investment to grow from
                    its beginning balance to its ending balance, assuming the
                    profits were reinvested at the end of each yearly period of
                    the investment's life span.
                  `),
  },
  sharpe: {
    text: "Sharpe",
    tooltip:
      FormatText.stripWhitespace(`The Sharpe ratio is a measure of risk-adjusted return. It
                    describes how much excess return you receive for the added
                    volatility that you tolerate for holding a riskier asset. 
                    A ratio of 1 to 2 is considered decent, a ratio under 1 is considered
                    average or poor, and a ratio above 2 is considered
                    excellent.
                  `),
  },
  maxDrawdown: {
    text: "Max Drawdown",
    tooltip:
      FormatText.stripWhitespace(`A maximum drawdown (MDD) is the maximum observed percentage
                    loss from a peak to a trough of a portfolio. Maximum
                    drawdown is an indicator of downside risk over a specified
                    time period.
                  `),
  },
  dPercentage: {
    text: "Day",
    tooltip:
      FormatText.stripWhitespace(`The Daily Percentage Change (D%) measures the percentage
                    difference in the value of a portfolio for the last 24
                    hours. It provides insights into the portfolio's
                    short-term performance and how it reacts to daily market
                    fluctuations. D% is commonly used by traders and investors
                    to assess the portfolio's daily volatility and can be
                    an essential indicator for short-term trading strategies.
                  `),
  },
  wPercentage: {
    text: "Week",
    tooltip:
      FormatText.stripWhitespace(`The Weekly Percentage Change (W%) measures the percentage
                    difference in the value of a portfolio for the last 7 days.
                    It provides a short-term view of the portfolio's
                    performance and is especially useful for investors who want
                    to evaluate the portfolio's weekly growth and assess
                    whether it aligns with their investment objectives.
                  `),
  },
  mPercentage: {
    text: "Month",
    tooltip:
      FormatText.stripWhitespace(`The Monthly Percentage Change (M%) measures the percentage
                    difference in the value of a portfolio for the last 30 days.
                    It provides a broader perspective on the portfolio's
                    performance, smoothing out short-term daily fluctuations and
                    focusing on longer-term trends. M% is valuable for investors
                    to evaluate the portfolio's monthly growth and assess
                    whether it aligns with their investment objectives.
                  `),
  },
  yPercentage: {
    text: "Year",
    tooltip:
      FormatText.stripWhitespace(`The Yearly Percentage Change (Y%) measures the percentage
                    difference in the value of a portfolio for the last 365
                    days. It provides a long-term view of the portfolio's
                    performance and is especially crucial for assessing the
                    portfolio's performance over different market cycles.
                    Y% helps investors understand how their portfolio has grown
                    or declined over a year period, guiding their decisions on
                    whether to adjust their investment strategies.
                  `),
  },
  ytdPercentage: {
    text: "YTD",
    tooltip:
      FormatText.stripWhitespace(`The Year-to-Date Percentage Change (YTD%) measures the
                    percentage difference in the value of a portfolio from the
                    beginning of the current year to the present date. It
                    provides insights into the portfolio's performance since the
                    start of the year and can help investors understand how
                    their portfolio has grown or declined over the year-to-date
                    period. YTD% is valuable for evaluating the portfolio's
                    performance over the current year and can guide investors in
                    making informed decisions about their investment strategies.
                    `),
  },
  tournamentPercentage: {
    text: "Tournament Return",
    tooltip:
      FormatText.stripWhitespace(`The Tournament Percentage Change measures the percentage
                    difference in the value of a portfolio during the tournament
                    period. It provides insights into the portfolio's
                    performance during the tournament and can help investors
                    understand how their portfolio has performed against other
                    portfolios in the competition.
                  `),
  },
  tPercentage: {
    text: "Total Return",
    tooltip: FormatText.stripWhitespace(`Total return is a crucial metric for
                    investors who want to assess the overall return of their
                    investment strategy since the inception of their portfolio.
                    By considering the impact of market fluctuations,
                    contributions, and withdrawals, provides a holistic view
                    of the portfolio's performance over the entire
                    investment journey.
                  `),
  },
  age: {
    text: "Age",
    tooltip:
      FormatText.stripWhitespace(`The Age of the portfolio refers to the time since the
                    portfolio was created. It provides insights into the portfolio's
                    investment journey and can help investors understand the
                    portfolio's performance over different market cycles and
                    economic conditions. 
                  `),
  },
  tournament: {
    text: "Tournament",
    tooltip:
      FormatText.stripWhitespace(`Tournament is a competition among a group of portfolios
                    that compete against each other to achieve the highest
                    return. The tournament is divided into several rounds, and
                    the portfolios with the highest returns advance to the next
                    round. The tournament continues until the final round, where
                    the portfolio with the highest return is declared the
                    winner.
                  `),
  },
};

export const portfolioVisibility: Array<{
  value: ContentVisibility;
  description: string;
}> = [
  {
    value: ContentVisibility.PUBLIC,
    description: "Everyone can see this portfolio",
  },
  {
    value: ContentVisibility.PRIVATE,
    description: "Only you can see this portfolio",
  },
  {
    value: ContentVisibility.FOLLOWERS,
    description: "Only followers can see this portfolio",
  },
  {
    value: ContentVisibility.SUBSCRIBERS,
    description: "Only subscribers can see this portfolio",
  },
];

export const PortfolioConfig = {
  oldestDataPoint: Date.now() - Intervals.MONTH * 6,
} as const;
